import { useState } from "react";
import { Check, X, Lock, TrendingUp, Shuffle } from "lucide-react";
import { scrollToId } from "../data/siteData";

const FIXED = [
  { ok: true, t: "Rate locked for 12–36 months" },
  { ok: true, t: "Predictable budgets, no market spike surprises" },
  { ok: true, t: "Protection when wholesale prices rise" },
  { ok: true, t: "Ideal for multi-year financial planning" },
  { ok: false, t: "Won't benefit if market prices fall mid-term" },
];

const VARIABLE = [
  { ok: true, t: "Month-to-month flexibility, easy exit" },
  { ok: true, t: "Can benefit during falling markets" },
  { ok: false, t: "Exposed to price spikes, bills can jump 30%+" },
  { ok: false, t: "Where most auto-renewed contracts end up" },
  { ok: false, t: "Turns annual budgeting into guesswork" },
];

export default function RateComparison() {
  const [sel, setSel] = useState("fixed");

  const Card = ({ type, title, icon: Icon, items }) => (
    <button
      type="button"
      onClick={() => setSel(type)}
      data-testid={`rate-toggle-${type}`}
      className={`text-left rounded-2xl p-6 sm:p-8 border-2 transition-all duration-300 ${
        sel === type
          ? "border-[#0066FF] bg-white shadow-[0_18px_45px_-8px_rgba(0,102,255,0.25)] -translate-y-1"
          : "border-slate-200 bg-white/60 hover:border-slate-300"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className={`inline-flex h-12 w-12 items-center justify-center rounded-xl transition-colors ${
          sel === type ? "bg-[#0066FF] text-white" : "bg-[#E6EEF7] text-[#0066FF]"
        }`}>
          <Icon size={24} />
        </div>
        
      </div>
      <h3 className="mt-5 font-display text-xl font-bold text-[#0B1F44]">{title}</h3>
      <ul className="mt-4 space-y-2.5">
        {items.map((i) => (
          <li key={i.t} className="flex items-start gap-2.5 text-sm text-slate-600">
            {i.ok ? (
              <Check size={17} className="mt-0.5 shrink-0 text-[#0066FF]" />
            ) : (
              <X size={17} className="mt-0.5 shrink-0 text-rose-400" />
            )}
            {i.t}
          </li>
        ))}
      </ul>
    </button>
  );

  return (
    <section id="rates" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-s font-bold uppercase tracking-[0.2em] text-[#0066FF]">Know Your Options</p>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B1F44]">
            Fixed rate vs variable rate: Pros and Cons
          </h2>
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            Variable rates might be seam appealing. And we agree, nothing beats their flexibility. <br></br>But during market spikes and rising energy rates, Fixed rates offer shelter against cost surges.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card type="fixed" title="Fixed-Rate Contract" icon={Lock} items={FIXED} />
          <Card type="variable" title="Variable / Index Rate" icon={TrendingUp} items={VARIABLE} />
        </div>

        <div className="mt-6 rounded-2xl border-l-4 border-[#F59E0B] bg-amber-50 p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#F59E0B]/15 text-[#F59E0B]">
            <Shuffle size={22} />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-[#0B1F44]">BlueSettle Hybrid Strategy</h4>
            <p className="mt-1 text-sm text-slate-600 leading-relaxed">
              For larger facilities, we can structure layered contracts, protecting portions of your load with fixed rates
              at different times to balance price certainty with market opportunity. No single
              structure fits every business; we recommend based on your usage profile.
            </p>
          </div>
          <button
            onClick={() => scrollToId("get-quote")}
            data-testid="rates-discuss-cta"
            className="shrink-0 rounded-full bg-[#0B1F44] hover:bg-[#0066FF] text-white text-sm font-semibold px-6 py-3 transition-colors"
          >
            See What's Right For Me
          </button>
        </div>
      </div>
    </section>
  );
}
